import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, Phone, Sparkles } from "lucide-react";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import FinalCTA from "../components/FinalCTA";
import NotFound from "./NotFound";
import { SERVICE_PAGES } from "../data/services";
import { SITE } from "../data/site";
import { trackEvent } from "../utils/analytics";

const STEPS = [
  { n: "01", title: "Skicka din förfrågan", desc: "Berätta vad du behöver – det tar under en minut." },
  { n: "02", title: "Vi återkommer med ett förslag", desc: "Snabbt svar med ett tydligt upplägg – kostnadsfritt och utan förpliktelser." },
  { n: "03", title: "Vi utför jobbet", desc: "Vi kommer som överenskommet och lämnar inget förrän resultatet är kristallklart." },
];

export default function ServicePage() {
  const { slug } = useParams();
  const s = SERVICE_PAGES[slug];
  if (!s) return <NotFound />;

  return (
    <div data-testid="service-page" className="bg-white">
      <Seo title={s.seo.title} description={s.seo.description} path={`/tjanster/${s.slug}`} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-ice blur-3xl opacity-70" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className={s.image ? "lg:col-span-6" : "lg:col-span-8"}>
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue">{s.overline}</p>
            </Reveal>
            <Reveal delay={0.06}>
              <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08] text-brand-ink">{s.h1}</h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl">{s.intro}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to={`/boka-offert?tjanst=${s.slug}`}
                  data-testid={`service-cta-${s.slug}`}
                  onClick={() => trackEvent("boka_click", { location: `service_${s.slug}` })}
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue text-white font-semibold px-8 py-4 hover:bg-brand-blueHover hover:-translate-y-0.5 hover:shadow-[0_14px_35px_rgba(14,98,254,0.35)] active:translate-y-0 transition-all duration-300"
                >
                  Boka {s.navLabel.toLowerCase()} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <a href={SITE.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand-ink border border-slate-200 font-semibold px-8 py-4 hover:border-brand-blue hover:text-brand-blue transition-all duration-300">
                  <Phone className="h-4 w-4" /> {SITE.phone}
                </a>
              </div>
            </Reveal>
          </div>
          {s.image && (
            <div className="lg:col-span-6">
              <Reveal delay={0.2}>
                <div className="overflow-hidden rounded-[2rem] shadow-[0_30px_60px_-20px_rgba(7,22,44,0.25)] ring-1 ring-slate-900/5 group">
                  <img src={s.image} alt={s.imageAlt} className="w-full aspect-[4/5] max-h-[480px] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]" loading="lazy" />
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      {/* LEVELS (only fönsterputs) */}
      {s.levels && (
        <section className="bg-brand-iceBg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <Reveal>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-brand-ink">Tre nivåer av fönsterputs</h2>
              <p className="mt-3 text-slate-600">{s.levelsIntro}</p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {s.levels.map((lvl, i) => (
                <Reveal key={lvl.id} delay={i * 0.07}>
                  <div className="h-full rounded-2xl bg-white border border-slate-100 shadow-[0_2px_10px_rgba(7,22,44,0.04)] p-6">
                    <div className="flex items-center gap-2.5">
                      <h3 className="font-heading text-lg font-bold text-brand-ink">{lvl.name}</h3>
                      {lvl.badge && <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-md bg-brand-blue text-white">{lvl.badge}</span>}
                    </div>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{lvl.short}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            {s.addon && (
              <Reveal delay={0.2}>
                <div className="mt-6 rounded-2xl border border-dashed border-brand-blue/40 bg-brand-ice p-6 flex items-start gap-4" data-testid="service-addon-box">
                  <span className="h-10 w-10 rounded-full bg-brand-blue/15 flex items-center justify-center shrink-0">
                    <Sparkles className="h-5 w-5 text-brand-blue" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h3 className="font-semibold text-brand-ink">{s.addon.title}</h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.addon.text}</p>
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* INCLUDES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <Reveal>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-brand-ink">Detta ingår</h2>
        </Reveal>
        <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {s.includes.map((item, i) => (
            <Reveal key={item} delay={i * 0.06}>
              <li className="flex items-start gap-3.5 rounded-2xl bg-brand-iceBg p-5 h-full">
                <span className="h-8 w-8 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0">
                  <Check className="h-4 w-4 text-brand-blue" strokeWidth={2.5} />
                </span>
                <span className="text-brand-ink font-medium">{item}</span>
              </li>
            </Reveal>
          ))}
        </ul>
      </section>

      {/* PROCESS */}
      <section className="bg-brand-iceBg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <Reveal>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-brand-ink">Så går det till</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((st, i) => (
              <Reveal key={st.n} delay={i * 0.08}>
                <div>
                  <span className="font-heading text-5xl font-extrabold text-brand-blue/15 select-none">{st.n}</span>
                  <h3 className="mt-2 font-heading text-lg font-bold text-brand-ink tracking-tight">{st.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{st.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MINI FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <Reveal>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-brand-ink">Vanliga frågor om {s.navLabel.toLowerCase()}</h2>
        </Reveal>
        <div className="mt-8 space-y-5">
          {s.faq.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.06}>
              <div className="rounded-2xl border border-slate-100 bg-white shadow-[0_2px_10px_rgba(7,22,44,0.04)] p-6" data-testid={`service-faq-${i}`}>
                <h3 className="font-semibold text-brand-ink">{f.q}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
